import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import { AssetUrls } from 'config/assets';
import { useUnbanUserMutation } from 'store/services/admin/adminApi';
import './UnbanUserModal.css';

interface UnbanUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  username: string;
  banInfo: {
    bannedAt: string;
    bannedBy: {
      username: string;
    };
    banReason: string;
  };
  onSuccess: () => void;
}

const UnbanUserModal: React.FC<UnbanUserModalProps> = ({
  isOpen,
  onClose,
  userId,
  username,
  banInfo,
  onSuccess
}) => {
  const [note, setNote] = useState<string>('');
  const [unbanUser, { isLoading }] = useUnbanUserMutation();

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setNote('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    try {
      await unbanUser({
        userId,
        body: {
          note: note.trim() || undefined
        }
      }).unwrap();

      toast.success(`${username} has been unbanned and can now log in.`);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to unban user:', error);
      toast.error(error.data?.message || 'Failed to unban user. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const modalContent = (
    <div className="unban-user-modal-overlay" onClick={onClose}>
      <div className="unban-user-modal" onClick={(e) => e.stopPropagation()}>
        <div className="unban-user-modal-header">
          <h2 className="unban-user-modal-title">Unban User Account</h2>
          <button className="unban-user-close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="unban-user-modal-content">
          <div className="success-banner">
            ✓ You are about to restore access for <strong>{username}</strong>
          </div>

          <div className="user-info-section">
            <div className="info-row">
              <span className="info-label">User:</span>
              <span className="info-value username-value">{username}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Banned On:</span>
              <span className="info-value">{formatDate(banInfo.bannedAt)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Banned By:</span>
              <span className="info-value">{banInfo.bannedBy.username}</span>
            </div>
          </div>

          <div className="ban-reason-section">
            <h3 className="section-subtitle">Original Ban Reason:</h3>
            <div className="ban-reason-text">
              {banInfo.banReason}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="note" className="form-label">
              Unban Note
              <span className="label-optional">(Optional)</span>
            </label>
            <textarea
              id="note"
              className="form-textarea"
              placeholder="Add a note about why the user was unbanned (optional, max 500 characters)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={500}
              disabled={isLoading}
            />
            {note.length > 0 && (
              <CharacterCounter
                currentLength={note.length}
                maxLength={500}
                warningThreshold={450}
              />
            )}
          </div>

          <div className="effects-list">
            <h3 className="section-subtitle">What happens when a user is unbanned:</h3>
            <ul>
              <li>User will be able to log in immediately</li>
              <li>All platform features will be restored</li>
              <li>Ban history will be preserved for audit purposes</li>
              <li>This action will be logged in the admin audit trail</li>
              <li>User will not receive an automatic notification (consider contacting them)</li>
            </ul>
          </div>

          <div className="warning-box">
            ℹ️ <strong>Note:</strong> The ban history will remain in the database for record-keeping,
            but the user's account will be fully restored.
          </div>
        </div>

        <div className="unban-user-modal-footer">
          <MagicButton
            label="Cancel"
            color="grey"
            onPress={onClose}
            isDisabled={isLoading}
            fullWidth={false}
          />
          <MagicButton
            label={isLoading ? 'Unbanning User...' : 'Unban User'}
            color="purple"
            onPress={handleSubmit}
            isLoading={isLoading}
            isDisabled={isLoading}
            fullWidth={false}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default UnbanUserModal;
